import * as THREE from 'three';
import { cloth, emis, leather, metal, std } from '../materials.ts';
import { G, M, V, glowSprite, limb, ring } from '../prims.ts';
import { figure } from './heroes.ts';
import { muleBody } from './sendas.ts';
import { begin, done, type BaseOpts, type Model } from './types.ts';

/*
 * The cleric's last road, chapter 2 (docs/sendas/clerigo.md, «La pasarela»): its people, painted miniatures.
 *
 * - The muleteer of the crag (`arriero`): weathered, a wool cap pulled low, a sheepskin over a leather jerkin, the old
 *   scar Tessa stitched crooked across his forehead (a pale line); his lame mule beside him, one hind hoof lifted, two
 *   milk churns slung on its packsaddle, the lead rope in his hand.
 * - Joran (`joran`): Tessa's son, the apothecary of Musgoviejo, fifty-five, tall and lean, a long dark coat buttoned to
 *   the neck, a grey scarf, his apothecary's bag of worn leather with its brass clasp at his hip, thinning grey hair,
 *   a lantern of his own in his hand.
 * - His two apprentices (`apprentice`, `apprenticeB`): young, in the shop's slate smocks under hooded cloaks, each with a
 *   lantern on a short pole.
 *
 * Colors: the south face's cold greys and browns, the lanterns' light white; never gold (the bag's clasp is pale steel).
 */

const SKIN = '#cfa58c';
const SKIN_WEATHER = '#b98c74';
const SKIN_PALE = '#d6b4a0';
const IRON = '#2c3036';

/** A small lantern of the road (the cage, the glass, a white flame: a glow, no light). */
const lantern = (parent: THREE.Object3D, at: THREE.Vector3, size = 1): THREE.Group => {
  const l = G(parent, at.x, at.y, at.z);
  l.scale.setScalar(size);
  const iron = metal(IRON, { roughness: 0.5 });
  M(new THREE.CylinderGeometry(0.05, 0.07, 0.03, 8), iron, l).position.y = 0.09;
  M(new THREE.CylinderGeometry(0.07, 0.055, 0.025, 8), iron, l).position.y = -0.09;
  M(new THREE.CylinderGeometry(0.05, 0.05, 0.15, 10), std('#e9eef6', { roughness: 0.15, emissive: '#d6e2f4', emissiveIntensity: 0.9 }), l);
  M(new THREE.SphereGeometry(0.026, 8, 6), emis('#ffffff', '#f4f8ff', 2.4), l);
  glowSprite(l, '#eef4ff', 0.8, V(0, 0, 0), 0.8);
  return l;
};

/** The face's eyes and nose on a head frame. */
const face = (h: THREE.Object3D, nose: string): void => {
  for (const k of [-1, 1]) M(new THREE.SphereGeometry(0.012, 5, 4), std('#141214'), h).position.set(k * 0.042, 1.656, 0.114);
  const n = M(new THREE.ConeGeometry(0.017, 0.045, 5), std(nose, { roughness: 0.85 }), h);
  n.position.set(0, 1.628, 0.13);
  n.rotation.x = Math.PI / 2 + 0.2;
};

/** The muleteer and his lame mule. */
const arriero = (o?: BaseOpts): Model => {
  const c = begin(o, 0.9, 891);
  const skin = std(SKIN_WEATHER, { roughness: 0.9 });
  const jerkin = leather('#4a3a2c');
  const fleece = std('#c2c6cc', { roughness: 1 });
  const f = figure({ legs: cloth('#3a3632'), boots: leather('#231c17'), torso: jerkin, shoulders: fleece, arms: cloth('#45403a'), forearms: cloth('#45403a'), hands: skin, skin });
  c.g.add(f.fig);
  f.fig.position.x = -0.38;
  f.fig.scale.set(1.02, 0.98, 1.02);
  const b = f.bodyAbs;
  const h = f.headAbs;
  // The sheepskin over the shoulders, rough at its edge.
  for (let i = 0; i < 10; i++) {
    const a = (i / 10) * Math.PI * 2;
    M(new THREE.SphereGeometry(0.05, 6, 5), fleece, b).position.set(Math.cos(a) * 0.19, 1.38, Math.sin(a) * 0.15);
  }
  // A wool cap pulled low, slate; grey stubble; the crooked scar across the forehead.
  const capM = cloth('#3f4652');
  const cap = M(new THREE.SphereGeometry(0.132, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.5), capM, h);
  cap.position.set(0, 1.672, -0.01);
  ring(h, 0.13, 0.026, capM, 1.665, 4, 12);
  const scar = M(new THREE.BoxGeometry(0.075, 0.009, 0.01), std('#e6d3c8', { roughness: 0.7 }), h);
  scar.position.set(0.01, 1.7, 0.118);
  scar.rotation.z = 0.35;
  M(new THREE.SphereGeometry(0.1, 10, 8, 0, Math.PI * 2, Math.PI * 0.55, Math.PI * 0.45), std('#6d6862', { roughness: 1 }), h).position.set(0, 1.6, 0.03);
  face(h, '#a87c64');
  // The lead rope from his left hand to the mule's halter.
  const lh = G(f.lhAbs, -0.28, 0.91, 0.13);
  limb(lh, V(0, 0, 0), V(0.5, 0.3, 0.55), 0.008, 0.008, std('#6a5a48', { roughness: 1 }), 4);
  f.rig.armL.rotation.x = -0.4;
  // The mule, to his right, looking the same way; one hind hoof up (lame).
  const mule = muleBody(c.g, '#5a4c44');
  mule.position.set(0.42, 0, 0.1);
  mule.scale.setScalar(0.9);
  // The packsaddle and two milk churns of pale tin.
  M(new THREE.BoxGeometry(0.42, 0.08, 0.5), leather('#3a2c20'), mule).position.set(0, 1.02, 0);
  const tin = metal('#9aa1aa', { roughness: 0.45 });
  for (const k of [-1, 1]) {
    const churn = M(new THREE.CylinderGeometry(0.1, 0.12, 0.34, 10), tin, mule);
    churn.position.set(k * 0.3, 0.88, 0);
    M(new THREE.CylinderGeometry(0.05, 0.06, 0.06, 8), tin, mule).position.set(k * 0.3, 1.08, 0);
  }
  // (The lame hoof: a little block of shadow lifted off the ground under its rear right leg.)
  M(new THREE.CylinderGeometry(0.04, 0.045, 0.06, 7), std('#1a1614'), mule).position.set(0.12, 0.14, -0.38);
  return done(c, 1.8, () => undefined, f.rig);
};

/** Joran, the apothecary: tall, the long dark coat, the bag, his own lantern. */
const joran = (o?: BaseOpts): Model => {
  const c = begin(o, 0.62, 892);
  const coat = cloth('#262a31');
  const skin = std(SKIN_PALE, { roughness: 0.85 });
  const f = figure({ legs: cloth('#22252b'), boots: leather('#1a1613'), torso: coat, shoulders: coat, arms: coat, forearms: coat, hands: skin, skin });
  c.g.add(f.fig);
  f.fig.scale.set(0.92, 1.08, 0.92);
  const b = f.bodyAbs;
  const h = f.headAbs;
  // The coat long to the shins, buttoned to the neck: a row of small pale buttons; a grey scarf.
  const skirt = M(new THREE.CylinderGeometry(0.19, 0.27, 0.62, 12), coat, b);
  skirt.position.y = 0.74;
  for (let i = 0; i < 5; i++) M(new THREE.SphereGeometry(0.014, 5, 4), metal('#aeb4bc', { roughness: 0.4 }), b).position.set(0, 1.42 - i * 0.12, 0.2);
  ring(b, 0.13, 0.04, cloth('#7d838c'), 1.47, 5, 12);
  // The bag at his right hip, worn leather, its pale steel clasp, the strap across him.
  const bag = M(new THREE.BoxGeometry(0.1, 0.2, 0.26), leather('#4a3526'), b);
  bag.position.set(0.25, 0.92, 0.02);
  M(new THREE.BoxGeometry(0.02, 0.05, 0.06), metal('#b8bec6', { roughness: 0.35 }), b).position.set(0.31, 0.99, 0.02);
  const strap = M(new THREE.BoxGeometry(0.03, 0.75, 0.02), leather('#3a2a1e'), b);
  strap.position.set(0.05, 1.22, 0.19);
  strap.rotation.z = 0.62;
  // Thin grey hair, a tired face, its brows down.
  const hair = std('#8c8f94', { roughness: 1 });
  const top = M(new THREE.SphereGeometry(0.127, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.34), hair, h);
  top.position.set(0, 1.66, -0.02);
  for (const k of [-1, 1]) {
    M(new THREE.BoxGeometry(0.05, 0.07, 0.07), hair, h).position.set(k * 0.115, 1.6, -0.03);
    const brow = M(new THREE.BoxGeometry(0.04, 0.01, 0.012), std('#5c5a58'), h);
    brow.position.set(k * 0.044, 1.684, 0.112);
    brow.rotation.z = k * 0.25;
  }
  face(h, '#c4a08c');
  // His lantern in his left hand.
  lantern(G(f.lhAbs, -0.29, 0.74, 0.16), V(0, 0, 0), 1);
  f.rig.armL.rotation.x = -0.25;
  f.rig.head.rotation.x = 0.1;
  return done(c, 1.92, () => undefined, f.rig);
};

/** An apprentice of the apothecary's: young, the slate smock, a hooded cloak, a lantern on a short pole. */
const apprentice = (o: BaseOpts | undefined, seed: number, cloak: string, hairC: string): Model => {
  const c = begin(o, 0.55, seed);
  const smock = cloth('#3f4652');
  const skin = std(SKIN, { roughness: 0.85 });
  const f = figure({ legs: cloth('#2c2f35'), boots: leather('#221c18'), torso: smock, shoulders: cloth(cloak), arms: smock, forearms: smock, hands: skin, skin, cape: cloak, capeLen: 0.9, capeR: 0.32 });
  c.g.add(f.fig);
  f.fig.scale.set(0.86, 0.92, 0.86);
  const h = f.headAbs;
  // The hood down on the shoulders, the hair under it.
  ring(f.bodyAbs, 0.15, 0.05, cloth(cloak), 1.47, 5, 12);
  M(new THREE.SphereGeometry(0.13, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.5), std(hairC, { roughness: 1 }), h).position.set(0, 1.66, -0.015);
  face(h, '#c49a84');
  // The pole over the shoulder, the lantern swinging at its end.
  const pole = G(f.rhAbs, 0.28, 0.9, 0.14);
  limb(pole, V(0, -0.05, 0), V(0, 0.9, 0.25), 0.014, 0.014, std('#4a3e34', { roughness: 0.8 }), 5);
  const l = lantern(pole, V(0, 0.78, 0.34), 0.9);
  const tick = (n: number): void => {
    l.rotation.x = Math.sin(n * 1.3 + seed) * 0.12;
  };
  f.rig.armR.rotation.x = -0.5;
  return done(c, 1.65, tick, f.rig);
};

/** The cleric's chapter 2 people by name (null: not one of them). */
export const pasarelaModel = (id: string, o?: BaseOpts): Model | null => {
  switch (id) {
    case 'arriero':
      return arriero(o);
    case 'joran':
      return joran(o);
    case 'apprentice':
      return apprentice(o, 893, '#4a4f58', '#3a2a20');
    case 'apprenticeB':
      return apprentice(o, 894, '#3b3f47', '#6a5540');
    default:
      return null;
  }
};
